import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import classnames from 'classnames/bind';
import { Row, Col, Steps, Icon, Button } from 'grab-ui';
import { ExceptMobile, Mobile } from 'components/v2/responsive/responsive';
import DivButton from 'components/v2/div-button/div-button';
import BookAgainTable from 'components/v2/book-again-table/book-again-table';
import BookAgainMobile from 'views/v2/home/book-again-mobile';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import { getCompanyID } from 'helper/user';
import {
  fetchCompletedDeliveriesAction,
  fetchOngoingDeliveriesIntervalAction,
  paxFetchOngoingDeliveriesIntervalAction,
  stopFetchOngoingDeliveriesIntervalAction,
  stopPaxFetchOngoingDeliveriesIntervalAction,
  paxFetchCompletedDeliveriesAction,
} from 'actions/history-actions';
import {
  bookAgainAction,
  updateOrderShallowAction,
} from 'actions/booking-actions';
import {
  enableAnytimeBookingUIBookingSelector,
  isPaxFromUserInfo,
  userInfoSelector,
} from 'selectors/app-selector';
import { reverseOrderPOIs } from 'helper/address-search';
import {
  checkBookAgainable,
  getOrderStatusToRender,
  fetchPreviousBookings,
} from 'helper/history-order';
import { GE_ORDER_STATE, GE_WEB_ORDER_STATE } from 'constants/order-constants';
import {
  GFB_ORDER_SUMMARIZE_FETCH_DAYS,
  GFB_RECENT_BOOKINGS_FETCH_LIMIT,
} from 'constants/history-constants.js';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_HOME_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import { getDeliveryID, isShopifyOrder } from 'helper/order';
import { shopifyBookAgainAction } from 'actions/shopify-actions';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck.js';
import { SuggestedPlacesVertical } from 'views/v2/saved-poi/suggested-places';

import css from './order-summarize.less';

const cx = classnames.bind(css);
const Step = Steps.Step;

const ONGOING_WIDGET_SOURCE = 'ongoing';
const UPCOMING_WIDGET_SOURCE = 'upcoming';

class OrderSummarize extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    const {
      fetchCompletedDeliveries,
      fetchOngoingDeliveriesInterval,
      userInfo,
    } = this.props;
    const companyID = getCompanyID(userInfo);
    const fetchParams = isPaxUser()
      ? {
          fetchPreviousBookingsAction: fetchCompletedDeliveries,
          // 0,0 means fetch all recent orders, up to 40
          startTimestamp: 0,
          endTimestamp: 0,
        }
      : {
          fetchPreviousBookingsAction: fetchCompletedDeliveries,
          fetchDays: GFB_ORDER_SUMMARIZE_FETCH_DAYS,
          fetchLimit: GFB_RECENT_BOOKINGS_FETCH_LIMIT,
          companyID,
        };
    fetchPreviousBookings(fetchParams);
    fetchOngoingDeliveriesInterval({ companyID });
  }

  componentWillUnmount() {
    // consider to use useEffect next time
    const { stopFetchOngoingDeliveriesInterval } = this.props;
    stopFetchOngoingDeliveriesInterval();
  }

  onClickOngoingOrders = (state, source) => () => {
    const {
      history,
      userInfo,
      countingOngoingOrders,
      scribeExpressHomePageWebViewRecentUpdates,
    } = this.props;
    const companyID = getCompanyID(userInfo);
    const label =
      source && source === ONGOING_WIDGET_SOURCE
        ? 'VIEW_ONGOING'
        : 'VIEW_UPCOMING';

    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, label, {
      [custom.COMPANY_ID]: companyID,
      ONGOING_BOOKINGS: countingOngoingOrders.total,
    });

    if (label === 'VIEW_ONGOING') {
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
        eventName: SCRIBE_HOME_EVENTS.VIEW_ONGOING,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ONGOING_BOOKINGS]: countingOngoingOrders.total,
        },
      });
    }

    if (label === 'VIEW_UPCOMING') {
      const { latestOrderStateChanges } = this.props;
      const firstTwoOrders = latestOrderStateChanges.slice(0, 2);

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
        eventName: SCRIBE_HOME_EVENTS.VIEW_UPCOMING,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.ORDER_ID]: firstTwoOrders.map(o => o.deliveryID),
        },
      });

      scribeExpressHomePageWebViewRecentUpdates();
    }

    history.push({
      pathname: ROUTES_V2_PATHS.bookings,
      state: { ongoingState: state },
    });
  };

  onClickBookAgain = order => async () => {
    const {
      userInfo,
      onClickBookAgain: onClickBookAgainProp,
      bookAgain,
      updateOrderShallow,
      history,
      enableAnytimeBookingUIBooking,
      anytimeBookingBookAgain,
    } = this.props;
    const shopifyOrder = isShopifyOrder(_get(order, 'thirdPartySource'));

    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'BOOK_AGAIN', {
      [custom.COMPANY_ID]: companyID,
      ORDER_ID: getDeliveryID(order),
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
      eventName: SCRIBE_HOME_EVENTS.BOOK_AGAIN,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.ORDER_ID]: getDeliveryID(order),
      },
    });

    if (enableAnytimeBookingUIBooking && !shopifyOrder) {
      anytimeBookingBookAgain(order, () => {
        history.push(ROUTES_V2_PATHS.newBooking);
      });
      return;
    }

    updateOrderShallow({}); // clear old order in redux
    const isPax = isPaxFromUserInfo(userInfo);
    const pois = await reverseOrderPOIs(order, companyID, isPax);

    if (shopifyOrder) {
      this.props.shopifyBookAgain({
        orders: _get(order, 'shopifyMetaData').map(({ orderID, step_idx }) => ({
          orderID,
          address: _get(pois, `[${step_idx}]`),
        })),
      });
      history.push({
        pathname: ROUTES_V2_PATHS.shopifyHome,
      });
      return;
    }

    onClickBookAgainProp(() => {
      bookAgain({ order, pois });
    });
  };

  onClickViewAllBookAgain = () => {
    const { history, userInfo, completedOrders } = this.props;

    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'VIEW_PAST_BOOKINGS', {
      [custom.COMPANY_ID]: companyID,
    });

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
      eventName: SCRIBE_HOME_EVENTS.VIEW_PAST_BOOKINGS,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.PAST_BOOKINGS]: completedOrders
          ? completedOrders.map(o => o.deliveryID)
          : [],
      },
    });

    history.push({
      pathname: ROUTES_V2_PATHS.bookings,
      state: { completedState: '' },
    });
  };

  renderOngoingOrders = () => {
    const { countingOngoingOrders } = this.props;

    if (countingOngoingOrders.total === 0) {
      return null;
    }

    const processing = countingOngoingOrders.processing || '--';
    const allocating = countingOngoingOrders.allocating || '--';
    const pickingUp = countingOngoingOrders.pickingUp || '--';
    const droppingOff = countingOngoingOrders.droppingOff || '--';

    return (
      <Fragment>
        <div className={css.sectionHeader}>
          <h5>
            {I18n.t('v2.homepage.orderOverview.ongoingBooking.title', {
              amt: countingOngoingOrders.total,
            })}
          </h5>
          <DivButton
            onClick={this.onClickOngoingOrders(
              GE_WEB_ORDER_STATE.ActiveDelivery,
              ONGOING_WIDGET_SOURCE,
            )}
          >
            {I18n.t('v2.homepage.orderOverview.viewAll')}
          </DivButton>
        </div>
        <div className={css.ongoingOrderContent}>
          <Row type="flex" className={css.orderCounts}>
            <Col md={6} xs={12}>
              <DivButton
                className={css.sectionContainer}
                onClick={this.onClickOngoingOrders(
                  GE_WEB_ORDER_STATE.ActiveDelivery,
                  ONGOING_WIDGET_SOURCE,
                )}
              >
                <h4>{processing}</h4>
                <h6>{I18n.t('v2.orderState.geWebState.SCHEDULED')}</h6>
              </DivButton>
            </Col>
            <Col md={6} xs={12}>
              <DivButton
                className={css.sectionContainer}
                onClick={this.onClickOngoingOrders(
                  GE_WEB_ORDER_STATE.FindingDriver,
                  ONGOING_WIDGET_SOURCE,
                )}
              >
                <h4>{allocating}</h4>
                <h6>{I18n.t('v2.orderState.geWebState.ALLOCATING')}</h6>
              </DivButton>
            </Col>
            <Col md={6} xs={12}>
              <DivButton
                className={css.sectionContainer}
                onClick={this.onClickOngoingOrders(
                  GE_ORDER_STATE.PickingUp,
                  ONGOING_WIDGET_SOURCE,
                )}
              >
                <h4>{pickingUp}</h4>
                <h6>{I18n.t('v2.orderState.geState.PICKING_UP')}</h6>
              </DivButton>
            </Col>
            <Col md={6} xs={12}>
              <DivButton
                className={css.sectionContainer}
                onClick={this.onClickOngoingOrders(
                  GE_ORDER_STATE.DroppingOff,
                  ONGOING_WIDGET_SOURCE,
                )}
              >
                <h4>{droppingOff}</h4>
                <h6>{I18n.t('v2.orderState.geState.DROPPING_OFF')}</h6>
              </DivButton>
            </Col>
          </Row>
          <ExceptMobile>
            <Steps className={css.step}>
              <Step className={css.step1} icon={<Icon type="reload" />} />
              <Step className={css.step2} icon={<Icon type="clock-circle" />} />
              <Step
                className={css.step3}
                icon={<img src={require('public/images/upload.svg')} alt="" />}
              />
              <Step
                className={css.step4}
                icon={
                  <img src={require('public/images/download.svg')} alt="" />
                }
              />
              <Step className={css.step1} icon={<Icon type="reload" />} />
            </Steps>
          </ExceptMobile>
        </div>
      </Fragment>
    );
  };

  renderRecentUpdate = () => {
    const { latestOrderStateChanges } = this.props;
    if (_isEmpty(latestOrderStateChanges)) return null;
    const firstTwoOrders = latestOrderStateChanges.slice(0, 2);

    return (
      <Fragment>
        <div className={css.sectionHeader}>
          <h5>{I18n.t('v2.homepage.orderOverview.latestUpdate')}</h5>
          <DivButton
            onClick={this.onClickOngoingOrders(
              GE_WEB_ORDER_STATE.ActiveDelivery,
              UPCOMING_WIDGET_SOURCE,
            )}
          >
            {I18n.t('v2.homepage.orderOverview.viewAll')}
          </DivButton>
        </div>
        <div className={css.recentUpdateContent}>
          {firstTwoOrders.map(order => {
            const isDroppingOff =
              order.status === GE_ORDER_STATE.DroppingOff ||
              order.status === GE_ORDER_STATE.PendingDropOff;
            const img = isDroppingOff ? (
              <img src={require('public/images/download.svg')} alt="" />
            ) : (
              <img src={require('public/images/upload.svg')} alt="" />
            );
            const text = `${getOrderStatusToRender(order)}${
              order.bookingCode ? `∙ ${order.bookingCode}` : ''
            }`;
            const recipient = _get(order, 'steps[1].contact.name');

            return (
              <DivButton
                key={order.bookingCode}
                className={css.recentUpdateItem}
                onClick={this.onClickOngoingOrders(
                  GE_WEB_ORDER_STATE.ActiveDelivery,
                )}
              >
                <Row type="flex" className={css.leftPanel}>
                  <div
                    className={cx('icon', {
                      'icon-droppingoff': isDroppingOff,
                      'icon-pickingup': !isDroppingOff,
                    })}
                  >
                    {img}
                  </div>
                  <div className={css.recipientInfo}>
                    <h5>{`${I18n.t('v2.commonButton.to')} ${recipient}`}</h5>
                    <h6>{text}</h6>
                  </div>
                </Row>
                <Icon type="right" />
              </DivButton>
            );
          })}
        </div>
      </Fragment>
    );
  };

  renderBookAgain = () => {
    const { completedOrders, isCompletedOrdersLoading } = this.props;

    return (
      <div className={css.bookAgain}>
        <div className={css.sectionHeader}>
          <h5>{I18n.t('v2.homepage.orderOverview.bookAgain')}</h5>
          <DivButton onClick={this.onClickViewAllBookAgain}>
            {I18n.t('v2.homepage.orderOverview.viewAll')}
          </DivButton>
        </div>
        <Mobile>
          <BookAgainMobile
            completedOrders={completedOrders}
            onClickBookAgain={this.onClickBookAgain}
          />
        </Mobile>
        <ExceptMobile>
          <BookAgainTable
            orders={completedOrders}
            className={css.orderTable}
            loading={isCompletedOrdersLoading}
            action={order => {
              const isDisabled = !checkBookAgainable(order);

              return (
                <Button
                  onClick={this.onClickBookAgain(order)}
                  role="button"
                  tabIndex="0"
                  onKeyDown={null}
                  className={css.bookAgainBtn}
                  disabled={isDisabled}
                >
                  {I18n.t('v2.homepage.orderOverview.bookAgain')}
                </Button>
              );
            }}
            pagination={{ pageSize: 10 }}
          />
        </ExceptMobile>
      </div>
    );
  };

  render() {
    return (
      <div className={css.orderSummarize}>
        <Row type="flex" className={css.ongoingOrder}>
          <Col md={16} xs={24} sm={24}>
            {this.renderOngoingOrders()}
          </Col>
          <Col md={8} xs={24} sm={24} className={css.recentUpdate}>
            {this.renderRecentUpdate()}
          </Col>
        </Row>
        <Row type="flex" className={css.ongoingOrder}>
          <Mobile>
            <SuggestedPlacesVertical />
          </Mobile>
        </Row>
        {this.renderBookAgain()}
      </div>
    );
  }
}

OrderSummarize.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  completedOrders: PropTypes.arrayOf(PropTypes.shape({})),
  countingOngoingOrders: PropTypes.shape({}),
  latestOrderStateChanges: PropTypes.arrayOf(PropTypes.shape({})),
  bookAgain: PropTypes.func.isRequired,
  shopifyBookAgain: PropTypes.func.isRequired,
  onClickBookAgain: PropTypes.func.isRequired,
  updateOrderShallow: PropTypes.func.isRequired,
  fetchCompletedDeliveries: PropTypes.func.isRequired,
  fetchOngoingDeliveriesInterval: PropTypes.func.isRequired,
  stopFetchOngoingDeliveriesInterval: PropTypes.func.isRequired,
  anytimeBookingBookAgain: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  isCompletedOrdersLoading: PropTypes.bool.isRequired,
  enableAnytimeBookingUIBooking: PropTypes.bool,
  scribeExpressHomePageWebViewRecentUpdates: PropTypes.func.isRequired,
};

OrderSummarize.defaultProps = {
  completedOrders: [],
  countingOngoingOrders: {},
  latestOrderStateChanges: [],
  enableAnytimeBookingUIBooking: false,
};

const mapStateToProps = state => ({
  userInfo: userInfoSelector(state),
  completedOrders: state.history.completedOrders,
  isCompletedOrdersLoading: state.history.isCompletedOrdersLoading,
  countingOngoingOrders: state.history.countingOngoingOrders,
  latestOrderStateChanges: state.history.latestOrderStateChanges,
  enableAnytimeBookingUIBooking: enableAnytimeBookingUIBookingSelector(state),
});

// Do not change to the shorthand object return
// Using function form so that this runs when the component is instantiated
const mapDispatchToProps = dispatch => ({
  shopifyBookAgain: args => dispatch(shopifyBookAgainAction(args)),
  bookAgain: args => dispatch(bookAgainAction(args)),
  updateOrderShallow: args => dispatch(updateOrderShallowAction(args)),
  fetchCompletedDeliveries: args => {
    const fetchAction = isPaxUser()
      ? paxFetchCompletedDeliveriesAction
      : fetchCompletedDeliveriesAction;
    return dispatch(fetchAction(args));
  },
  fetchOngoingDeliveriesInterval: args => {
    const fetchAction = isPaxUser()
      ? paxFetchOngoingDeliveriesIntervalAction
      : fetchOngoingDeliveriesIntervalAction;
    return dispatch(fetchAction(args));
  },
  stopFetchOngoingDeliveriesInterval: args => {
    const stopAction = isPaxUser()
      ? stopPaxFetchOngoingDeliveriesIntervalAction
      : stopFetchOngoingDeliveriesIntervalAction;
    return dispatch(stopAction(args));
  },
  anytimeBookingBookAgain: (order, callback) =>
    dispatch(NewBookingDuck.creators.rebook(order, callback)),
  scribeExpressHomePageWebViewRecentUpdates: () =>
    dispatch(ScribeDuck.creators.scribeExpressHomePageWebViewRecentUpdates()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OrderSummarize),
);
